"use client";

import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Check, ChevronDown, MousePointer2, Plus } from "lucide-react";
import type { Persona } from "@/components/landing/WelcomeDialog";
import { LOCALES, LOCALE_NAMES, localePath, type Locale } from "@/lib/i18n";
import { capture, clearAnalyticsConsent } from "@/lib/analytics";

// ============================================================
// Shared, content-agnostic landing building blocks.
// Both the private (/) and business (/foretag) pages compose
// these widgets with their own copy / videos / data.
// ============================================================

// Country flags from Flagpack, with Flagpack's signature rounded corners and
// hairline border. Sized via the `className` height (width stays auto / 4:3).
const FLAG_SRC: Record<Locale, string> = {
  sv: "/flags/se.svg",
  en: "/flags/us.svg",
  no: "/flags/no.svg",
  da: "/flags/dk.svg",
  fi: "/flags/fi.svg",
  de: "/flags/de.svg",
  cs: "/flags/cz.svg",
  zh: "/flags/cn.svg",
};

export function Flag({ locale = "sv", className }: { locale?: Locale; className?: string }) {
  return (
    <img
      src={FLAG_SRC[locale]}
      alt=""
      aria-hidden
      className={`inline-block w-auto shrink-0 rounded-[2px] ring-1 ring-black/10 ${className ?? ""}`}
    />
  );
}

// Kept for Swedish-only pages (flyer, SEO landing pages).
export function SwedishFlag({ className }: { className?: string }) {
  return <Flag locale="sv" className={className} />;
}

// Render a copy string, swapping *...* segments for the serif italic accent
// and \n for line breaks. Keeps bilingual dictionaries as plain strings.
export function renderAccented(
  text: string,
  accentClass = "font-newsreader font-normal italic",
) {
  return text.split("\n").map((line, lineIndex, lines) => (
    <Fragment key={lineIndex}>
      {line.split(/(\*[^*]+\*)/g).map((part, i) =>
        part.startsWith("*") && part.endsWith("*") ? (
          <span key={i} className={accentClass}>
            {part.slice(1, -1)}
          </span>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
      {lineIndex < lines.length - 1 ? <br /> : null}
    </Fragment>
  ));
}

const PERSONA_LABELS: Record<Locale, { private: string; business: string }> = {
  sv: { private: "Privatperson", business: "Företag" },
  en: { private: "Homeowner", business: "Business" },
  no: { private: "Privatperson", business: "Bedrift" },
  da: { private: "Privatperson", business: "Virksomhed" },
  fi: { private: "Yksityishenkilö", business: "Yritys" },
  de: { private: "Privatperson", business: "Unternehmen" },
  cs: { private: "Domácnost", business: "Firma" },
  zh: { private: "个人", business: "企业" },
};

const COOKIE_SETTINGS_LABELS: Record<Locale, string> = {
  sv: "Cookieinställningar",
  en: "Cookie settings",
  no: "Informasjonskapsler",
  da: "Cookieindstillinger",
  fi: "Evästeasetukset",
  de: "Cookie-Einstellungen",
  cs: "Nastavení cookies",
  zh: "Cookie 设置",
};

// Footer entry that reopens the consent choice. Withdrawing consent has to be
// as easy as giving it (art. 7.3 GDPR), so this sits next to the policy links
// on every page rather than behind a settings screen.
export function CookieSettingsLink({ locale = "sv" }: { locale?: Locale }) {
  return (
    <button
      type="button"
      onClick={clearAnalyticsConsent}
      className="transition-colors hover:text-white/70"
    >
      {COOKIE_SETTINGS_LABELS[locale]}
    </button>
  );
}

// Audience segment toggle in the top utility bar. `active` reflects the
// current view; `onSelect` decides what happens (the pages route between
// / and /foretag).
export function PersonaToggle({
  active,
  onSelect,
  locale = "sv",
}: {
  active: Persona;
  onSelect: (persona: Persona) => void;
  locale?: Locale;
}) {
  const labels = PERSONA_LABELS[locale];
  return (
    <div className="flex items-center gap-3 sm:gap-4">
      <a
        href={localePath(locale, "/")}
        onClick={() => { capture("persona_toggled", { persona: "private", from: active }); onSelect("private"); }}
        aria-current={active === "private" ? "page" : undefined}
        className={`text-[0.75rem] tracking-tight transition-colors hover:text-white ${
          active === "private" ? "text-white" : "text-white/50"
        }`}
      >
        {labels.private}
      </a>
      <span className="h-3 w-px bg-white/20" aria-hidden="true" />
      <a
        href={localePath(locale, "/foretag")}
        onClick={() => { capture("persona_toggled", { persona: "business", from: active }); onSelect("business"); }}
        aria-current={active === "business" ? "page" : undefined}
        className={`text-[0.75rem] tracking-tight transition-colors hover:text-white ${
          active === "business" ? "text-white" : "text-white/50"
        }`}
      >
        {labels.business}
      </a>
    </div>
  );
}

const LANGUAGE_LABELS: Record<Locale, string> = {
  sv: "Språk",
  en: "Language",
  no: "Språk",
  da: "Sprog",
  fi: "Kieli",
  de: "Sprache",
  cs: "Jazyk",
  zh: "语言",
};

/**
 * Language picker: shows the current locale, opens a dropdown of supported
 * languages on click. `path` is the canonical (Swedish) path of the current
 * page — e.g. "/", "/foretag", "/company" — so each option links to the
 * matching localized URL.
 *
 * `variant`:
 *   - "bar"    – compact trigger for the top utility bar
 *   - "menu"   – full-width row for the mobile sheet
 */
export function LanguageSwitcher({
  locale,
  path,
  variant = "bar",
  tone = "dark",
  compact = false,
}: {
  locale: Locale;
  path: string;
  variant?: "bar" | "menu";
  tone?: "dark" | "light";
  /** Bar variant only: show just the flag below the 2xl breakpoint. */
  compact?: boolean;
}) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const isLight = tone === "light";

  const select = (next: Locale) => {
    detailsRef.current?.removeAttribute("open");
    if (next === locale) {
      return;
    }
    capture("language_switched", { from: locale, to: next });
  };

  const isMenu = variant === "menu";

  return (
    // Native details/summary works before React hydration as well as after it.
    <details
      ref={detailsRef}
      className={`group/language relative ${isMenu ? "w-full" : "z-50"}`}
    >
      <summary
        aria-label={LANGUAGE_LABELS[locale]}
        className={`list-none cursor-pointer [&::-webkit-details-marker]:hidden ${
          isMenu
            ? "flex w-full items-center justify-between px-5 py-4 text-left text-[1.0625rem] text-white/85 transition-colors active:bg-white/10"
            : isLight
              ? "inline-flex items-center gap-1.5 text-[0.75rem] tracking-tight text-black/45 transition-colors hover:text-black"
              : "inline-flex items-center gap-1.5 text-[0.75rem] tracking-tight text-white/60 transition-colors hover:text-white"
        }`}
      >
        <span className={`inline-flex items-center ${isMenu ? "gap-2.5" : "gap-1.5"}`}>
          <Flag locale={locale} className={isMenu ? "h-4" : "h-3"} />
          {isMenu ? (
            LANGUAGE_LABELS[locale]
          ) : (
            <span className={compact ? "hidden 2xl:inline" : undefined}>{LOCALE_NAMES[locale]}</span>
          )}
        </span>
        {isMenu ? (
          <span className="inline-flex items-center gap-1.5 text-[0.9375rem] text-white/40">
            {LOCALE_NAMES[locale]}
            <ChevronDown
              size={14}
              strokeWidth={2}
              className="transition-transform duration-200 group-open/language:rotate-180"
            />
          </span>
        ) : (
          <ChevronDown
            size={12}
            strokeWidth={2}
            className="transition-transform duration-200 group-open/language:rotate-180"
          />
        )}
      </summary>

      <div
        role="listbox"
        aria-label={LANGUAGE_LABELS[locale]}
        className={
          isMenu
            ? "mx-5 mb-3 overflow-hidden rounded-xl border border-white/10 bg-neutral-900 divide-y divide-white/10"
            : isLight
              ? "absolute right-0 top-full z-[100] mt-1.5 min-w-[10.5rem] overflow-hidden rounded-lg border border-black/10 bg-white py-1 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.18)]"
              : "absolute right-0 top-full z-[100] mt-1.5 min-w-[10.5rem] overflow-hidden rounded-lg border border-white/15 bg-neutral-950 py-1 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.85)]"
        }
      >
        {LOCALES.map((option) => {
          const active = option === locale;
          return (
            <a
              key={option}
              role="option"
              aria-selected={active}
              href={localePath(option, path)}
              onClick={() => select(option)}
              className={
                isMenu
                  ? `flex items-center justify-between px-4 py-3 text-[0.9375rem] transition-colors active:bg-white/10 ${
                      active ? "text-white" : "text-white/70"
                    }`
                  : isLight
                    ? `flex items-center justify-between gap-3 px-3 py-2.5 text-[0.8125rem] transition-colors hover:bg-black/[0.04] ${
                        active ? "text-black" : "text-black/60"
                      }`
                    : `flex items-center justify-between gap-3 px-3 py-2.5 text-[0.8125rem] transition-colors hover:bg-white/10 ${
                        active ? "text-white" : "text-white/70"
                      }`
              }
            >
              <span className="inline-flex items-center gap-2">
                <Flag locale={option} className="h-3.5" />
                {LOCALE_NAMES[option]}
              </span>
              {active && (
                <Check
                  size={14}
                  strokeWidth={2.25}
                  className={isLight && !isMenu ? "text-black/50" : "text-white/70"}
                />
              )}
            </a>
          );
        })}
      </div>
    </details>
  );
}

// Background video that plays only while its containing card is hovered.
// Drop it as the first child of a card; it auto-wires to that card's hover.
// How much to trim off the end of an `idleAtEnd` clip so it freezes while the
// notification is still on screen, just before its exit animation. Tweak if
// the resting frame lands too early/late.
const END_TRIM_SECONDS = 0.9;

function HoverVideo({
  src,
  className,
  active,
  // When true the idle preview rests on the *last* frame (instead of the
  // first), so a short clip reads as "finished" until hovered, then resets
  // and replays. Used by the contained (non full-bleed) cards.
  idleAtEnd = false,
}: {
  src: string;
  className?: string;
  active?: boolean;
  idleAtEnd?: boolean;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const idleAtEndRef = useRef(idleAtEnd);
  idleAtEndRef.current = idleAtEnd;
  useEffect(() => {
    const video = ref.current;
    const card = video?.parentElement;
    if (!video || !card) return;

    // The clip animates the notification in and then back out at the very end.
    // Freeze a touch before the true end so it rests with the notification
    // still on screen rather than on the empty outro frame.
    const restTime = () => {
      const d = video.duration || 0;
      return Math.max(0, d - END_TRIM_SECONDS);
    };

    // Park on a decoded frame for the idle state: the resting frame when
    // `idleAtEnd` (notification visible), otherwise a nudge past the start so
    // it's a real preview rather than a black box.
    const showFrame = () => {
      try {
        video.currentTime = idleAtEndRef.current ? restTime() : 0.1;
      } catch {
        /* not seekable yet */
      }
    };
    video.addEventListener("loadedmetadata", showFrame);

    // While playing an idle-at-end clip, stop a hair before the end so the
    // notification's exit never shows — it holds on the resting frame until
    // the next hover replays it from the start.
    const onTimeUpdate = () => {
      if (!idleAtEndRef.current) return;
      const d = video.duration || 0;
      if (d && video.currentTime >= d - END_TRIM_SECONDS) {
        video.pause();
        try {
          video.currentTime = restTime();
        } catch {
          /* not seekable yet */
        }
      }
    };
    video.addEventListener("timeupdate", onTimeUpdate);

    // Always replay from the start so the full notification animation plays
    // through on every hover.
    const play = () => {
      video.currentTime = 0;
      void video.play().catch(() => {});
    };
    const pause = () => {
      video.pause();
      showFrame();
    };
    card.addEventListener("mouseenter", play);
    card.addEventListener("mouseleave", pause);
    return () => {
      video.removeEventListener("loadedmetadata", showFrame);
      video.removeEventListener("timeupdate", onTimeUpdate);
      card.removeEventListener("mouseenter", play);
      card.removeEventListener("mouseleave", pause);
    };
  }, []);

  // Tap-driven playback for touch devices (no hover): play while the card is
  // toggled active, otherwise pause and rewind to the preview frame.
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (active) {
      video.currentTime = 0;
      void video.play().catch(() => {});
    } else {
      video.pause();
      try {
        video.currentTime = idleAtEnd
          ? Math.max(0, (video.duration || 0) - END_TRIM_SECONDS)
          : 0.1;
      } catch {
        /* not seekable yet */
      }
    }
  }, [active, idleAtEnd]);

  return (
    <>
      <video
        ref={ref}
        className={`${className ?? ""} brightness-[0.6] transition-[filter] duration-500 ease-out group-hover:brightness-100 group-data-[active=true]:brightness-100`}
        // `idleAtEnd` clips play through exactly once per hover and rest on the
        // final frame; the full-bleed cards keep looping while hovered.
        loop={!idleAtEnd}
        muted
        playsInline
        preload="metadata"
        aria-hidden
      >
        {src.endsWith(".webm") ? (
          <>
            <source src={src} type="video/webm" />
            <source src={src.replace(/\.webm$/, ".mp4")} type="video/mp4" />
          </>
        ) : (
          <source src={src} />
        )}
      </video>
      {/* Subtle tap/hover cue, top-left — fades out once the card is active */}
      <span className="pointer-events-none absolute left-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-0 group-data-[active=true]:opacity-0">
        <MousePointer2 size={15} strokeWidth={2} />
      </span>
    </>
  );
}

// Aceternity-style "Text Generate Effect": each word fades up and de-blurs in
// sequence. Driven purely by the parent card's `group` hover via staggered
// CSS transition delays — animates in on hover, reverses on leave.
function TextGenerateEffect({ text, className }: { text: string; className?: string }) {
  const words = text.split(" ");
  return (
    <p className={className}>
      {words.map((word, i) => (
        <span
          key={i}
          className="inline-block opacity-0 blur-[6px] transition-[opacity,filter] duration-500 ease-out group-hover:opacity-100 group-hover:blur-none group-data-[active=true]:opacity-100 group-data-[active=true]:blur-none"
          style={{ transitionDelay: `${i * 45}ms` }}
        >
          {word}
          {i < words.length - 1 ? "\u00A0" : ""}
        </span>
      ))}
    </p>
  );
}

// Plays a clip through exactly once, triggered the first time the element
// scrolls into view. Stays on the final frame afterwards and never replays.
export function InViewVideo({
  src,
  className,
}: {
  src: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let played = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !played) {
            played = true;
            video.currentTime = 0;
            void video.play().catch(() => {});
            observer.disconnect();
          }
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      className={className}
      src={src}
      muted
      playsInline
      preload="metadata"
      aria-hidden
    />
  );
}

// One of the three hero video cards in the bento grid. The glass label pill
// stays pinned at the bottom on hover and expands to reveal a longer
// explanation that streams in with the Text Generate Effect.
export function VideoBentoCard({
  video,
  icon,
  title,
  subtitle,
  description,
  // When the clip is smaller than the card, render it centered (object-contain)
  // on a solid black backdrop instead of filling the frame, and rest the idle
  // preview on its final frame until hovered.
  contain = false,
}: {
  video: string;
  icon: ReactNode;
  title: string;
  subtitle: string;
  description: string;
  contain?: boolean;
}) {
  const [active, setActive] = useState(false);
  return (
    <button
      type="button"
      data-active={active}
      onClick={() => {
        // On touch devices this toggles the card so the video plays and the
        // description reveals (the desktop hover has no equivalent on tap).
        setActive((v) => !v);
      }}
      className={`group relative flex h-[66svh] w-full flex-col items-center justify-end overflow-hidden rounded-[8px] p-5 text-left transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:h-[92svh] sm:max-h-[920px] ${
        contain ? "bg-black" : "bg-gradient-to-br from-[#262626] to-[#161616]"
      }`}
    >
      <HoverVideo
        className={`absolute inset-0 h-full w-full ${contain ? "object-contain" : "object-cover"}`}
        src={video}
        active={active}
        idleAtEnd={contain}
      />
      {/* Brand-green fade that bleeds down from the top edge of the card. */}
      {contain && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-[#214740] to-transparent" />
      )}
      {/* Gradient overlay for text readability */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />

      {/* Glass label — stays visible on hover, grows to reveal the description */}
      <div className="relative z-10 w-[26rem] max-w-full rounded-2xl border border-white/15 bg-white/10 px-6 py-4 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] backdrop-blur-md transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:bg-white/15 group-data-[active=true]:bg-white/15">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[5px] bg-black text-white">
            {icon}
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[0.875rem] font-medium text-white">{title}</span>
            <span className="text-[0.875rem] text-white/70">{subtitle}</span>
          </span>
        </div>
        <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:grid-rows-[1fr] group-data-[active=true]:grid-rows-[1fr]">
          <div className="overflow-hidden">
            <TextGenerateEffect
              text={description}
              className="mt-4 border-t border-white/10 pt-4 text-left text-[0.8125rem] leading-relaxed text-white/75"
            />
          </div>
        </div>
      </div>
    </button>
  );
}

// Word-by-word gray->white reveal that tracks scroll position. Each word
// brightens as the paragraph travels up through the viewport.
export function ScrollRevealText({ text, className }: { text: string; className?: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const wordRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const words = useMemo(() => text.split(" "), [text]);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // Reveal from when the block sits low in the viewport (85%) until it has
      // risen to the upper third (35%).
      const startY = vh * 0.85;
      const endY = vh * 0.35;
      const progress = Math.min(1, Math.max(0, (startY - rect.top) / (startY - endY)));
      const lit = progress * words.length;
      wordRefs.current.forEach((w, i) => {
        if (!w) return;
        const amt = Math.min(1, Math.max(0, lit - i));
        w.style.opacity = `${0.48 + amt * 0.52}`;
      });
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [words]);

  return (
    <p ref={ref} className={className}>
      {words.map((w, i) => (
        <span
          key={i}
          ref={(el) => {
            wordRefs.current[i] = el;
          }}
          className="text-white transition-opacity duration-150 ease-out"
          style={{ opacity: 0.48 }}
        >
          {w}
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </p>
  );
}

// ============================================================
// MOCKUP SHOWCASE – sticky stacked panels
// ============================================================
export interface MockupPanel {
  /** Headline; the word(s) inside *asterisks* render as the serif accent */
  heading: string;
  body: string;
  video: string;
  /** Desktop side for the TEXT block. Mobile is always image-over-text. */
  textSide: "left" | "right";
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
// Smoothstep – eases the linear scroll progress into a soft S-curve so the
// stack accelerates and settles instead of moving mechanically.
const smooth = (t: number) => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};
const SCROLL_PER_PANEL_VH = 160;

// Render a heading, swapping *...* segments for the serif accent
function renderMockupHeading(heading: string) {
  return heading.split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") ? (
      <span key={i} className="font-newsreader font-normal italic text-black/70">
        {part.slice(1, -1)}
      </span>
    ) : (
      part
    ),
  );
}

export function MockupShowcase({ panels }: { panels: MockupPanel[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const veilRef = useRef<HTMLDivElement>(null);
  const activeIndexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let frame = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const update = () => {
      frame = 0;
      const container = containerRef.current;
      if (!container || panels.length === 0) return;

      const lockedHeight = Number.parseFloat(
        getComputedStyle(container).getPropertyValue("--landing-screen-height"),
      );
      const vh =
        Number.isFinite(lockedHeight) && lockedHeight > 0
          ? lockedHeight
          : window.innerHeight || 1;
      const rect = container.getBoundingClientRect();
      const travel = Math.max(1, rect.height - vh);
      const progress = clamp(-rect.top / travel, 0, 1);
      const position = progress * Math.max(0, panels.length - 1);
      const visible = rect.bottom > 0 && rect.top < vh;
      const nextActiveIndex = visible ? Math.round(position) : -1;
      const sceneDistance = Math.abs(position - Math.round(position));
      const veilOpacity = reducedMotion
        ? 0
        : smooth(clamp((sceneDistance - 0.28) / 0.22, 0, 1));

      contentRefs.current.forEach((content, i) => {
        if (!content) return;
        const isActive = i === nextActiveIndex;
        content.style.visibility = isActive ? "visible" : "hidden";
        content.style.pointerEvents = isActive ? "auto" : "none";
      });

      if (veilRef.current) {
        veilRef.current.style.opacity = veilOpacity.toFixed(3);
      }

      videoRefs.current.forEach((v, i) => {
        if (!v) return;
        if (i === nextActiveIndex) {
          if (v.paused) void v.play().catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      });

      if (nextActiveIndex >= 0 && nextActiveIndex !== activeIndexRef.current) {
        activeIndexRef.current = nextActiveIndex;
        setActiveIndex(nextActiveIndex);
      }
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [panels.length]);

  return (
    <div
      ref={containerRef}
      className="relative bg-white"
      style={{
        height: `var(--landing-mockup-scroll-height, ${100 + Math.max(0, panels.length - 1) * SCROLL_PER_PANEL_VH}svh)`,
      }}
    >
      <div className="sticky top-0 h-[var(--landing-screen-height,100svh)] w-full overflow-hidden bg-white">
        {panels.map((panel, i) => (
          <section key={panel.heading} className="absolute inset-0">
            <div
              ref={(el) => {
                contentRefs.current[i] = el;
              }}
              className={`mx-auto flex h-full w-full max-w-6xl flex-col items-center justify-center gap-5 px-6 sm:px-8 md:gap-14 md:px-10 ${
                panel.textSide === "left" ? "md:flex-row" : "md:flex-row-reverse"
              }`}
              style={{ visibility: i === 0 ? "visible" : "hidden" }}
            >
              <div className="relative flex items-center justify-center md:min-h-0 md:flex-1 md:basis-1/2">
                <video
                  ref={(el) => {
                    videoRefs.current[i] = el;
                  }}
                  className="relative z-10 h-[var(--landing-mockup-video-height,38svh)] w-auto object-contain sm:h-[var(--landing-mockup-video-sm-height,44svh)] md:h-auto md:max-h-[var(--landing-mockup-video-max-height,84svh)]"
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden
                >
                  <source src={`${panel.video.replace(/\.webm$/, ".mp4")}?v=3`} type="video/mp4" />
                </video>
              </div>

              <div className="flex flex-col justify-center text-center md:flex-1 md:basis-1/2 md:text-left">
                <h2 className="text-balance font-display text-[2.125rem] font-normal leading-[1.02] tracking-[-0.025em] text-black sm:text-[2.5rem] md:text-[3.25rem]">
                  {renderMockupHeading(panel.heading)}
                </h2>
                <p className="mx-auto mt-4 max-w-[44ch] text-pretty text-[1.0625rem] leading-relaxed text-black/55 md:mx-0 md:mt-6 md:text-[1.25rem]">
                  {panel.body}
                </p>
              </div>
            </div>
          </section>
        ))}

        <div
          ref={veilRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 bg-white will-change-[opacity]"
          style={{ opacity: 0 }}
        />

        <div
          aria-hidden
          className="pointer-events-none absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full border border-black/[0.06] bg-white/90 px-3 py-2 backdrop-blur-sm md:bottom-auto md:left-auto md:right-7 md:top-1/2 md:-translate-y-1/2 md:translate-x-0 md:flex-col"
        >
          {panels.map((panel, i) => (
            <span
              key={panel.heading}
              className={`block rounded-full bg-black transition-[width,height,opacity] duration-300 ${
                i === activeIndex
                  ? "h-1.5 w-5 opacity-80 md:h-5 md:w-1.5"
                  : "h-1.5 w-1.5 opacity-20"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MockupPanelMedia({
  panel,
  videoRef,
}: {
  panel: MockupPanel;
  videoRef?: (el: HTMLVideoElement | null) => void;
}) {
  return (
    <div
      className={`mx-auto flex h-full w-full max-w-6xl flex-col items-center justify-center gap-5 px-6 sm:px-8 md:gap-14 md:px-10 ${
        panel.textSide === "left" ? "md:flex-row" : "md:flex-row-reverse"
      }`}
    >
      <div className="relative flex items-center justify-center md:min-h-0 md:flex-1 md:basis-1/2">
        <video
          ref={videoRef}
          className="relative z-10 h-[var(--landing-mockup-video-height,38svh)] w-auto object-contain sm:h-[var(--landing-mockup-video-sm-height,44svh)] md:h-auto md:max-h-[var(--landing-mockup-video-max-height,84svh)]"
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden
        >
          <source src={`${panel.video.replace(/\.webm$/, ".mp4")}?v=3`} type="video/mp4" />
        </video>
      </div>

      <div className="flex flex-col justify-center text-center md:flex-1 md:basis-1/2 md:text-left">
        <h2 className="text-balance font-display text-[2.125rem] font-normal leading-[1.02] tracking-[-0.025em] text-black sm:text-[2.5rem] md:text-[3.25rem]">
          {renderMockupHeading(panel.heading)}
        </h2>
        <p className="mx-auto mt-4 max-w-[44ch] text-pretty text-[1.0625rem] leading-relaxed text-black/55 md:mx-0 md:mt-6 md:text-[1.25rem]">
          {panel.body}
        </p>
      </div>
    </div>
  );
}

// Four independent full-screen scenes. Scroll past each one instead of
// pinning a sticky viewport and swapping content in place.
export function MockupSections({ panels }: { panels: MockupPanel[] }) {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sectionRefs.current.forEach((section, i) => {
      if (!section) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          const video = videoRefs.current[i];
          if (!video) return;
          if (entry.isIntersecting) {
            void video.play().catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        },
        { threshold: 0.45 },
      );
      observer.observe(section);
      observers.push(observer);
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, [panels.length]);

  return (
    <div className="bg-white">
      {panels.map((panel, i) => (
        <section
          key={panel.heading}
          ref={(el) => {
            sectionRefs.current[i] = el;
          }}
          className="relative flex h-[var(--landing-screen-height,100svh)] w-full items-center overflow-hidden"
        >
          <MockupPanelMedia
            panel={panel}
            videoRef={(el) => {
              videoRefs.current[i] = el;
            }}
          />
        </section>
      ))}
    </div>
  );
}

// ============================================================
// FAQ – accordion of common questions
// ============================================================
export interface FaqItem {
  q: string;
  a: string;
  linkHref?: string;
  linkLabel?: string;
}

export function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="mx-auto mt-12 max-w-3xl divide-y divide-black/10 border-y border-black/10 md:mt-16">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={i}>
            <button
              type="button"
              onClick={() => { if (!isOpen) capture("faq_item_opened", { question: item.q, index: i }); setOpen(isOpen ? null : i); }}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between gap-6 py-6 text-left"
            >
              <span className="font-sans text-[1.125rem] font-normal leading-snug tracking-[-0.01em] text-black md:text-[1.375rem]">
                {item.q}
              </span>
              <Plus
                size={22}
                strokeWidth={1.75}
                className={`shrink-0 text-black/40 transition-transform duration-300 ease-out ${
                  isOpen ? "rotate-45" : ""
                }`}
              />
            </button>
            <div
              className={`grid transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <p className="max-w-[60ch] pb-6 text-pretty text-[1rem] leading-relaxed text-black/55 md:text-[1.125rem]">
                  {item.a}
                </p>
                {item.linkHref && item.linkLabel && (
                  <a
                    href={item.linkHref}
                    className="mb-6 inline-flex items-center gap-1 text-[0.9375rem] font-medium text-black underline-offset-4 hover:underline"
                  >
                    {item.linkLabel}
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Renders supported brands as text chips. Shows the most popular `initial`
// brands; the remainder collapse behind a "+N fler" button that expands inline.
export function BrandList({
  brands,
  note,
  initial = 6,
  locale = "sv",
}: {
  brands: string[];
  note?: string;
  initial?: number;
  locale?: Locale;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? brands : brands.slice(0, initial);
  const remaining = brands.length - initial;

  return (
    <div className="mt-9">
      <div className="flex flex-wrap gap-2">
        {visible.map((name) => (
          <span
            key={name}
            className="inline-flex items-center rounded-full border border-black/10 bg-white px-3.5 py-1.5 text-[0.8125rem] font-medium text-neutral-700"
          >
            {name}
          </span>
        ))}
        {!expanded && remaining > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center rounded-full border border-black/10 bg-black/[0.04] px-3.5 py-1.5 text-[0.8125rem] font-medium text-neutral-600 transition-colors hover:bg-black/[0.08] hover:text-neutral-900"
          >
            +{remaining} {locale === "en" ? "more" : "fler"}
          </button>
        )}
      </div>
      {note && <p className="mt-4 text-[0.875rem] leading-tight text-neutral-500">{note}</p>}
    </div>
  );
}
